// One-shot Firestore reads (NO listeners — see src/firebase.ts). Every fetcher
// is shaped for React Query; keys live next to the calls in src/api/keys.ts.
// Queries always carry the filters the security rules prove against.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '../firebase'
import type {
  ApprovalCounters,
  AuditEntry,
  Availability,
  Credential,
  CredentialType,
  Department,
  Employee,
  Position,
  Shift,
  ShiftRequest,
  Team,
  Week,
  WeekTemplate,
} from '../lib/types'

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { ...(snap.data() as object), id: snap.id } as T
}

// ---------- reference data ----------

export async function fetchDepartment(departmentId: string): Promise<Department | null> {
  const snap = await getDoc(doc(db, 'departments', departmentId))
  return snap.exists() ? ({ ...(snap.data() as object), id: snap.id } as Department) : null
}

export async function fetchDepartments(): Promise<Department[]> {
  const snap = await getDocs(collection(db, 'departments'))
  return snap.docs.map((d) => withId<Department>(d))
}

export async function fetchTeams(departmentId: string): Promise<Team[]> {
  const snap = await getDocs(
    query(collection(db, 'teams'), where('departmentId', '==', departmentId), orderBy('sortOrder')),
  )
  return snap.docs.map((d) => withId<Team>(d))
}

export async function fetchPositions(departmentId: string): Promise<Position[]> {
  const snap = await getDocs(
    query(collection(db, 'positions'), where('departmentId', '==', departmentId), orderBy('sortOrder')),
  )
  return snap.docs.map((d) => withId<Position>(d))
}

export async function fetchCredentialTypes(departmentId: string): Promise<CredentialType[]> {
  const snap = await getDocs(
    query(collection(db, 'credentialTypes'), where('departmentId', '==', departmentId)),
  )
  return snap.docs.map((d) => withId<CredentialType>(d))
}

export async function fetchCounters(departmentId: string): Promise<ApprovalCounters | null> {
  const snap = await getDoc(doc(db, 'counters', departmentId))
  return snap.exists() ? (snap.data() as ApprovalCounters) : null
}

// ---------- roster (supervisor/director/admin only per rules) ----------

export async function fetchRoster(departmentId: string): Promise<Employee[]> {
  const snap = await getDocs(
    query(
      collection(db, 'employees'),
      where('departmentId', '==', departmentId),
      orderBy('workspace.displayName'),
    ),
  )
  return snap.docs.map((d) => d.data() as Employee)
}

export async function fetchNeedsSetup(): Promise<Employee[]> {
  const snap = await getDocs(query(collection(db, 'employees'), where('status', '==', 'needs_setup')))
  return snap.docs.map((d) => d.data() as Employee)
}

export async function fetchEmployee(email: string): Promise<Employee | null> {
  const snap = await getDoc(doc(db, 'employees', email))
  return snap.exists() ? (snap.data() as Employee) : null
}

// ---------- weeks + shifts ----------

export async function fetchWeekDoc(departmentId: string, weekStart: string): Promise<Week | null> {
  const snap = await getDoc(doc(db, 'weeks', `${departmentId}_${weekStart}`))
  return snap.exists() ? ({ ...(snap.data() as object), id: snap.id } as Week) : null
}

/** Scheduler grid: every shift in the department week, drafts included. */
export async function fetchDeptWeekShifts(departmentId: string, weekStart: string): Promise<Shift[]> {
  const snap = await getDocs(
    query(collection(db, 'shifts'), where('departmentId', '==', departmentId), where('weekId', '==', weekStart)),
  )
  return snap.docs.map((d) => withId<Shift>(d))
}

/** Employee team view: published shifts on the home team. */
export async function fetchTeamWeekShifts(teamId: string, weekStart: string): Promise<Shift[]> {
  const snap = await getDocs(
    query(
      collection(db, 'shifts'),
      where('teamId', '==', teamId),
      where('weekId', '==', weekStart),
      where('published', '==', true),
    ),
  )
  return snap.docs.map((d) => withId<Shift>(d))
}

export async function fetchMyShifts(email: string, fromDate: string): Promise<Shift[]> {
  const snap = await getDocs(
    query(
      collection(db, 'shifts'),
      where('assigneeEmail', '==', email),
      where('published', '==', true),
      where('date', '>=', fromDate),
      orderBy('date'),
    ),
  )
  return snap.docs.map((d) => withId<Shift>(d)).filter((s) => s.status === 'assigned')
}

export async function fetchOpenShifts(departmentId: string, fromDate: string): Promise<Shift[]> {
  const snap = await getDocs(
    query(
      collection(db, 'shifts'),
      where('departmentId', '==', departmentId),
      where('status', '==', 'open'),
      where('published', '==', true),
      where('date', '>=', fromDate),
      orderBy('date'),
    ),
  )
  return snap.docs.map((d) => withId<Shift>(d))
}

export async function fetchShift(shiftId: string): Promise<Shift | null> {
  const snap = await getDoc(doc(db, 'shifts', shiftId))
  return snap.exists() ? ({ ...(snap.data() as object), id: snap.id } as Shift) : null
}

export async function fetchTemplates(departmentId: string): Promise<WeekTemplate[]> {
  const snap = await getDocs(
    query(collection(db, 'templates'), where('departmentId', '==', departmentId), orderBy('updatedAt', 'desc')),
  )
  return snap.docs.map((d) => withId<WeekTemplate>(d))
}

// ---------- requests ----------

export async function fetchMyRequests(email: string): Promise<ShiftRequest[]> {
  const snap = await getDocs(
    query(collection(db, 'requests'), where('requesterEmail', '==', email), orderBy('createdAt', 'desc'), limit(50)),
  )
  return snap.docs.map((d) => withId<ShiftRequest>(d))
}

export async function fetchSwapsAwaitingMe(email: string): Promise<ShiftRequest[]> {
  const snap = await getDocs(
    query(collection(db, 'requests'), where('counterpartyEmail', '==', email), where('state', '==', 'pending_peer')),
  )
  return snap.docs.map((d) => withId<ShiftRequest>(d))
}

/** Approvals queue. Directors pass teamIds = null (whole department, incl. OT). */
export async function fetchApprovalsQueue(
  departmentId: string,
  teamIds: string[] | null,
): Promise<ShiftRequest[]> {
  if (teamIds === null) {
    const snap = await getDocs(
      query(
        collection(db, 'requests'),
        where('departmentId', '==', departmentId),
        where('state', '==', 'pending_approval'),
        orderBy('createdAt'),
      ),
    )
    return snap.docs.map((d) => withId<ShiftRequest>(d))
  }
  if (teamIds.length === 0) return []
  const snap = await getDocs(
    query(
      collection(db, 'requests'),
      where('departmentId', '==', departmentId),
      where('teamId', 'in', teamIds.slice(0, 10)),
      where('state', '==', 'pending_approval'),
      orderBy('createdAt'),
    ),
  )
  return snap.docs.map((d) => withId<ShiftRequest>(d))
}

/** Approved time-off overlapping a week — the builder's conflict overlay. */
export async function fetchApprovedTimeOff(
  departmentId: string,
  weekStart: string,
  weekEnd: string,
): Promise<ShiftRequest[]> {
  const snap = await getDocs(
    query(
      collection(db, 'requests'),
      where('departmentId', '==', departmentId),
      where('type', '==', 'timeoff'),
      where('state', '==', 'approved'),
      where('startDate', '<=', weekEnd),
      where('endDate', '>=', weekStart),
    ),
  )
  return snap.docs.map((d) => withId<ShiftRequest>(d))
}

// ---------- availability + credentials ----------

export async function fetchAvailability(email: string): Promise<Availability | null> {
  const snap = await getDoc(doc(db, 'availability', email))
  return snap.exists() ? (snap.data() as Availability) : null
}

export async function fetchDeptAvailability(departmentId: string): Promise<Availability[]> {
  const snap = await getDocs(
    query(collection(db, 'availability'), where('departmentId', '==', departmentId)),
  )
  return snap.docs.map((d) => d.data() as Availability)
}

export async function fetchMyCredentials(email: string): Promise<Credential[]> {
  const snap = await getDocs(
    query(collection(db, 'credentials'), where('employeeEmail', '==', email), orderBy('expiresAt')),
  )
  return snap.docs.map((d) => withId<Credential>(d))
}

export async function fetchDeptCredentials(departmentId: string): Promise<Credential[]> {
  const snap = await getDocs(
    query(collection(db, 'credentials'), where('departmentId', '==', departmentId), orderBy('expiresAt')),
  )
  return snap.docs.map((d) => withId<Credential>(d))
}

// ---------- audit (admin) ----------

export async function fetchAuditPage(
  departmentId: string | null,
  after: string | null,
  pageSize = 50,
): Promise<AuditEntry[]> {
  const parts = [orderBy('at', 'desc'), limit(pageSize)]
  const base = departmentId
    ? query(collection(db, 'auditLog'), where('departmentId', '==', departmentId), ...parts)
    : query(collection(db, 'auditLog'), ...parts)
  const q2 = after ? query(base, startAfter(after)) : base
  const snap = await getDocs(q2)
  return snap.docs.map((d) => withId<AuditEntry>(d))
}
